import { Component } from '@theme/component';
import { onAnimationEnd, yieldToMainThread, prefersReducedMotion } from '@theme/utilities';
import { ThemeEvents, FavoritesChangedEvent, FavoriteRemovedEvent } from '@theme/events';
import { Favorites } from '@theme/favorites';

/**
 * Shows/hides the visitor's favorited products. The grid contains every product, server-rendered
 * (so styling, vendor and Bio/Vegan badges match collection pages exactly), hidden by default;
 * this only toggles visibility based on localStorage, which Liquid cannot read.
 *
 * Removing a card fades it out first, then reflows the remaining cards into the gap (animated).
 * Undoing reverses that: the remaining cards shift back out of the way first, then the card fades
 * back in. The underlying favorited state always commits immediately (not after the animation), so
 * a fast undo click can't race with the pending removal.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} grid - The container holding every product's card (each hidden by default).
 * @property {HTMLElement} empty - The empty state, shown when there are no favorites.
 *
 * @extends {Component<Refs>}
 */
class FavoritesPage extends Component {
  requiredRefs = ['grid', 'empty'];

  /** @type {Set<HTMLElement>} Items currently mid hide/reveal-reflow, so a fast undo can't overlap with it. */
  #reflowing = new Set();

  connectedCallback() {
    super.connectedCallback();

    document.addEventListener(ThemeEvents.favoritesChanged, this.#onFavoritesChanged);
    this.#reassignOrders();
    for (const item of this.#allItems()) item.hidden = !Favorites.has(item.dataset.productHandle ?? '');
    this.#updateEmptyState();
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener(ThemeEvents.favoritesChanged, this.#onFavoritesChanged);
  }

  /**
   * Fades the card out, then (unless undone in the meantime) hides it and reflows the rest of the
   * grid into the gap it leaves behind.
   * @param {{handle: string}} data - The product handle to remove.
   * @param {Event} event - The click event.
   */
  removeFavorite = async (data, event) => {
    event.preventDefault();

    const item = this.#closestItem(event);

    // Already mid-fade from an earlier click on the same button; ignore the repeat.
    if (item?.classList.contains('is-removing')) return;

    document.dispatchEvent(
      new FavoriteRemovedEvent({
        handle: data.handle,
        title: item?.dataset.productTitle ?? '',
        image: item?.dataset.productImage ?? '',
        index: Favorites.getAll().indexOf(data.handle),
      })
    );

    item?.classList.add('is-removing');
    // Commits immediately so a fast undo click can't race with this method's own (delayed) state change.
    Favorites.toggle(data.handle);

    if (!item) return;

    // Yield first so the browser sees the class change before we look for animations to wait on.
    await yieldToMainThread();
    await onAnimationEnd(item);

    // Undone while fading: #onFavoritesChanged already cancelled the fade and removed the class.
    if (!item.classList.contains('is-removing')) return;

    await this.#hideWithReflow(item);
  };

  /**
   * Re-renders when favorites change anywhere on the page (including this page's own remove buttons).
   * @param {Event} event - The favorites changed event.
   */
  #onFavoritesChanged = (event) => {
    if (!(event instanceof FavoritesChangedEvent)) return;

    const { handle, isFavorite } = event.detail;
    const item = this.#findItem(handle);
    if (!item) return;

    if (isFavorite) {
      if (this.#reflowing.has(item)) return; // its own hide/reveal sequence is already handling this
      if (item.classList.contains('is-removing') && !item.hidden) {
        // Undo while still fading out: nothing has shifted yet, just cancel the fade.
        item.classList.remove('is-removing');
      } else if (item.hidden) {
        // Undo after the removal had already fully completed: reflow the rest back, then fade in.
        this.#revealWithReflow(item);
      }

      return;
    }

    if (item.classList.contains('is-removing') || item.hidden || this.#reflowing.has(item)) return; // already being handled elsewhere

    // Unfavorited via some other mechanism while this page happens to be open (e.g. another tab).
    item.hidden = true;
    item.style.order = '';
    this.#reassignOrders();
    this.#updateEmptyState();
  };

  /**
   * @param {Event} event
   * @returns {HTMLElement | null}
   */
  #closestItem(event) {
    return event.target instanceof Element ? event.target.closest('.favorites-page__item') : null;
  }

  /**
   * @param {string} handle
   * @returns {HTMLElement | null}
   */
  #findItem(handle) {
    return this.refs.grid.querySelector(`.favorites-page__item[data-product-handle="${CSS.escape(handle)}"]`);
  }

  /** @returns {HTMLElement[]} */
  #allItems() {
    return Array.from(this.refs.grid.querySelectorAll('.favorites-page__item'));
  }

  /** @param {HTMLElement} exclude */
  #visibleSiblings(exclude) {
    return this.#allItems().filter((item) => item !== exclude && !item.hidden);
  }

  #reassignOrders() {
    const handles = Favorites.getAll();

    for (const item of this.#allItems()) {
      const index = handles.indexOf(item.dataset.productHandle ?? '');
      if (index !== -1) item.style.order = String(index);
    }
  }

  #updateEmptyState() {
    this.refs.empty.hidden = this.#allItems().some((item) => !item.hidden);
  }

  /**
   * Hides the card and reflows its still-visible siblings into the gap it leaves, animated.
   * @param {HTMLElement} item
   */
  async #hideWithReflow(item) {
    this.#reflowing.add(item);

    const siblings = this.#visibleSiblings(item);
    const before = this.#captureRects(siblings);

    item.classList.remove('is-removing');
    item.hidden = true;
    item.style.order = '';
    this.#reassignOrders();
    this.#updateEmptyState();

    await this.#playFlip(siblings, before);

    this.#reflowing.delete(item);
  }

  /**
   * Reflows the still-visible siblings out of the way first, then reveals and fades the card in.
   * @param {HTMLElement} item
   */
  async #revealWithReflow(item) {
    this.#reflowing.add(item);

    const siblings = this.#visibleSiblings(item);
    const before = this.#captureRects(siblings);

    // Reserve its grid cell while staying invisible (opacity, not hidden) so the siblings' reflow
    // already accounts for it, without the card itself appearing yet.
    item.classList.add('is-removing');
    item.hidden = false;
    this.#reassignOrders();
    this.#updateEmptyState();

    await this.#playFlip(siblings, before);

    item.classList.remove('is-removing');
    this.#reflowing.delete(item);
  }

  /**
   * @param {HTMLElement[]} items
   * @returns {Map<HTMLElement, DOMRect>}
   */
  #captureRects(items) {
    return new Map(items.map((item) => [item, item.getBoundingClientRect()]));
  }

  /**
   * Classic FLIP: the DOM is already in its new state when this is called. Snaps each item back to
   * its pre-change position with no transition, then releases it next frame so it animates from
   * there to wherever it naturally landed.
   * @param {HTMLElement[]} items
   * @param {Map<HTMLElement, DOMRect>} before
   */
  async #playFlip(items, before) {
    if (prefersReducedMotion()) return;

    /** @type {HTMLElement[]} */
    const moved = [];

    for (const item of items) {
      const previous = before.get(item);
      if (!previous) continue;

      const next = item.getBoundingClientRect();
      const dx = previous.left - next.left;
      const dy = previous.top - next.top;

      if (dx === 0 && dy === 0) continue;

      item.style.transition = 'none';
      item.style.transform = `translate(${dx}px, ${dy}px)`;
      moved.push(item);
    }

    if (moved.length === 0) return;

    // Yield so the browser paints the snapped-back position before we release it into the transition.
    await yieldToMainThread();

    for (const item of moved) {
      item.style.transition = '';
      item.style.transform = '';
    }

    await onAnimationEnd(moved);
  }
}

if (!customElements.get('favorites-page')) {
  customElements.define('favorites-page', FavoritesPage);
}
